'use client';

import { useState } from 'react';
import JSZip from 'jszip';
import { parseFitFile } from '@/lib/fitParser';
import { pb } from '@/lib/pb';
import { UploadCloud, CheckCircle2, AlertCircle } from 'lucide-react';

export function GarminUploader({ onUploadComplete }: { onUploadComplete?: (data: any[]) => void }) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState('');
  const [stats, setStats] = useState({ total: 0, successful: 0, failed: 0 });

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsProcessing(true);
    setProgress(0);
    setStats({ total: 0, successful: 0, failed: 0 });
    setStatusText('Reading archive…');

    try {
      const zip = new JSZip();
      const loadedZip = await zip.loadAsync(file);
      const fitFiles: { name: string; getBuffer: () => Promise<ArrayBuffer> }[] = [];

      const processZip = async (currentZip: JSZip, prefix = '') => {
        for (const f of Object.values(currentZip.files)) {
          if (f.dir) continue;
          const lower = f.name.toLowerCase();
          if (lower.endsWith('.fit')) {
            fitFiles.push({ name: prefix + f.name, getBuffer: () => f.async('arraybuffer') });
          } else if (lower.endsWith('.zip')) {
            try {
              const nested = await JSZip.loadAsync(await f.async('arraybuffer'));
              await processZip(nested, prefix + f.name + '/');
            } catch {}
          }
        }
      };

      setStatusText('Scanning for .fit files…');
      await processZip(loadedZip);

      if (fitFiles.length === 0) { setStatusText('No .fit files found.'); setIsProcessing(false); return; }
      setStats(s => ({ ...s, total: fitFiles.length }));

      let ok = 0; let fail = 0;
      const allParsed: any[] = [];

      for (let i = 0; i < fitFiles.length; i++) {
        setStatusText(`Parsing ${i + 1} of ${fitFiles.length}…`);
        setProgress(Math.round((i / fitFiles.length) * 100));
        try {
          const parsed = await parseFitFile(await fitFiles[i].getBuffer());
          allParsed.push(parsed);
          ok++;
        } catch { fail++; }
        setStats(s => ({ ...s, successful: ok, failed: fail }));
      }

      setProgress(100);
      setStatusText('Done');
      if (onUploadComplete && allParsed.length > 0) {
        allParsed.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
        onUploadComplete(allParsed);
      }
    } catch { setStatusText('Failed to read archive.'); } finally { setIsProcessing(false); }
  };

  return (
    <div className="flex flex-col gap-4 p-5 rounded-2xl bg-card border border-border/60">
      <div>
        <p className="text-sm font-semibold tracking-tight">Import from .zip</p>
        <p className="text-xs text-muted-foreground mt-0.5">Upload your Garmin data export and we'll parse all .fit files locally.</p>
      </div>

      {!isProcessing && progress !== 100 && (
        <label className="flex flex-col items-center justify-center gap-2 h-28 border border-dashed border-border rounded-xl cursor-pointer hover:bg-muted/30 transition-colors">
          <UploadCloud className="w-6 h-6 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Click to select .zip</span>
          <input type="file" className="hidden" accept=".zip" onChange={handleFileUpload} disabled={isProcessing} />
        </label>
      )}

      {(isProcessing || progress > 0) && (
        <div className="space-y-3">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{statusText}</span>
            <span className="font-mono font-medium text-foreground">{progress}%</span>
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-foreground rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
          {stats.total > 0 && (
            <div className="grid grid-cols-3 gap-3 pt-1">
              {[
                { label: 'Found',  value: stats.total,      colour: 'text-foreground' },
                { label: 'Parsed', value: stats.successful, colour: 'text-emerald-500' },
                { label: 'Failed', value: stats.failed,     colour: 'text-rose-500' },
              ].map(s => (
                <div key={s.label} className="text-center">
                  <div className={`text-xl font-semibold ${s.colour}`}>{s.value}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5 uppercase tracking-wide">{s.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
